import React, { useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Users, Bell, ChevronRight, UserPlus, LogOut, Loader2, Share2, Settings, Plus, MessageSquare, Lock, ArrowRight, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, getDiscussionPath } from '../utils';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useCommunity } from '../hooks/useCommunity';
import RoomCard from '../components/RoomCard';
import DiscussionCard from '../components/DiscussionCard';
import Breadcrumbs from '../components/Breadcrumbs';

const CommunityHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const { 
    community, 
    loading, 
    isAdmin, 
    joinCommunity, 
    leaveCommunity 
  } = useCommunity(id);

  const [isJoining, setIsJoining] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [joinError, setJoinError] = useState('');

  const handleJoinLeave = async (e, providedPassword = null) => {
    if (!user) {
      navigate('/login', { state: { from: location } });
      return;
    }

    if (isAdmin) {
      navigate(`/c/${id}/settings`);
      return;
    }

    // If joining a private community
    if (!community?.is_joined && community?.privacy === 'private') {
      const isInviteOnly = community?.settings?.invite_only || false;
      
      // If invite only and not invited (and not provided password to bypass)
      if (isInviteOnly && !providedPassword) {
        if (community?.has_password) {
           setShowPasswordPrompt(true);
           return;
        } else {
           setIsRestricted(true);
           return;
        }
      }

      if (community?.has_password && !providedPassword) {
        setShowPasswordPrompt(true);
        return;
      }
    }

    setIsJoining(true);
    setJoinError('');
    try {
      if (community?.is_joined) {
        await leaveCommunity();
        addToast('Left community', 'success');
      } else {
        await joinCommunity(providedPassword);
        addToast('Joined community!', 'success');
        setShowPasswordPrompt(false);
        setPasswordInput('');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Action failed';
      setJoinError(msg);
      addToast(msg, 'error');
    } finally {
      setIsJoining(false);
    }
  };

  const onPasswordSubmit = (e) => {
    e.preventDefault();
    handleJoinLeave(e, passwordInput);
  };

  const handleInvite = () => {
    navigator.clipboard.writeText(window.location.href);
    addToast('Community link copied!', 'share');
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
      <Loader2 size={40} className="animate-spin mb-4 text-brand-indigo" />
      <p className="font-bold uppercase tracking-widest text-[10px]">Loading Hub...</p>
    </div>
  );

  if (!community) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
      <p className="font-bold text-lg mb-4 text-gray-900">Community not found.</p>
      <button onClick={() => navigate('/communities')} className="text-brand-indigo font-bold text-sm hover:underline uppercase tracking-widest">Explore Hubs</button>
    </div>
  );

  const activeRooms = (Array.isArray(community.rooms) ? community.rooms : []).filter(r => 
    r.status?.toLowerCase() === 'live' || r.status?.toLowerCase() === 'upcoming'
  );
  
  const communityRooms = activeRooms.slice(0, 4);
  const communityDiscussionsList = (Array.isArray(community.discussions) ? community.discussions : []).slice(0, 4);

  return (
    <div className="min-w-0">
      <AnimatePresence>
        {isRestricted && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Restricted Hub</h2>
              <p className="text-gray-500 font-medium mb-8 leading-relaxed text-sm">
                This community is invite-only. To join, you must be invited by an administrator or provide a valid access code if one was shared with you.
              </p>
              
              <button 
                onClick={() => setIsRestricted(false)}
                className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all flex items-center justify-center gap-2 text-sm"
              >
                Understood
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPasswordPrompt && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-brand-indigo/10 text-brand-indigo rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lock size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Private Hub</h2>
              <p className="text-gray-500 font-medium mb-8 text-sm">This community is private. Please enter the access code to join the hub.</p>
              
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <input 
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter password"
                    className={cn(
                      "w-full px-6 py-4 bg-gray-50 border-2 rounded-2xl focus:ring-4 focus:ring-brand-indigo/5 font-bold transition-all text-center text-xl tracking-widest outline-none",
                      joinError ? "border-red-100 bg-red-50 text-red-600 focus:ring-red-100" : "border-gray-50 focus:border-brand-indigo/20"
                    )}
                    autoFocus
                  />
                  {joinError && (
                    <p className="text-red-500 text-xs font-bold mt-3 animate-in fade-in slide-in-from-top-1">{joinError}</p>
                  )}
                </div>
                <button 
                  type="submit"
                  disabled={isJoining}
                  className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all flex items-center justify-center gap-2 text-sm"
                >
                  {isJoining ? <Loader2 size={20} className="animate-spin" /> : (
                    <>
                      Join Community
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setShowPasswordPrompt(false);
                    setPasswordInput('');
                    setJoinError('');
                  }}
                  className="w-full text-gray-400 font-bold py-2 text-[10px] uppercase tracking-[0.15em] hover:text-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
          <Breadcrumbs 
            items={[
              { label: 'Communities', path: '/communities' },
              { label: community.name }
            ]} 
          />
        </div>
        <div className="h-40 bg-brand-indigo relative overflow-hidden">
          {community.coverImage ? (
            <img src={community.coverImage} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-10 pb-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center md:items-end justify-between gap-4 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center md:items-end gap-4 w-full md:w-auto">
              <div className="w-28 h-28 rounded-[2rem] bg-white p-1.5 shadow-2xl">
                <div className={cn("w-full h-full rounded-[1.75rem] bg-brand-indigo flex items-center justify-center text-white text-3xl font-bold shadow-inner overflow-hidden")}>
                  {community.icon ? (
                    <img src={community.icon} alt={community.name} className="w-full h-full object-cover" />
                  ) : (
                    community.name.substring(0, 2).toUpperCase()
                  )}
                </div>
              </div>
              <div className="flex-1 pb-1">
                <div className="flex flex-col md:flex-row items-center gap-2 mb-1">
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{community.name}</h1>
                  <span className="bg-brand-indigo/10 text-brand-indigo text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-[0.15em] border border-brand-indigo/10">
                    {community.tagline || 'Verified Hub'}
                  </span>
                </div>
                <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mt-1">
                  <div className="flex items-center gap-1.5">
                    <Users size={14} className="text-gray-300" />
                    {community.members_count || 0} {community.members_count === 1 ? 'Member' : 'Members'}
                  </div>
                  <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                  <div className="flex items-center gap-1.5">
                    <MessageSquare size={14} className="text-gray-300" />
                    {(community.discussions || []).length} Discussions
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button 
                onClick={handleInvite}
                className="flex-1 md:flex-none p-3 bg-gray-50 text-gray-600 rounded-2xl font-bold hover:bg-gray-100 transition-all shadow-sm active:scale-95"
                title="Share Community"
              >
                <Share2 size={20} />
              </button>
              <button 
                className="flex-1 md:flex-none p-3 bg-gray-50 text-gray-600 rounded-2xl font-bold hover:bg-gray-100 transition-all shadow-sm active:scale-95"
                title="Notifications"
              >
                <Bell size={20} />
              </button>
              <button 
                onClick={handleJoinLeave}
                disabled={isJoining}
                className={cn(
                  "flex-1 md:flex-none px-8 py-3 rounded-2xl font-bold shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.15em]",
                  community.is_joined 
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200 shadow-gray-100" 
                    : "bg-brand-indigo text-white hover:bg-brand-indigo/90 shadow-brand-indigo/20"
                )}
              >
                {isJoining ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : isAdmin ? (
                  <>
                    <Settings size={18} />
                    Manage
                  </>
                ) : community.is_joined ? (
                  <>
                    <LogOut size={18} />
                    Leave
                  </>
                ) : (
                  <>
                    <UserPlus size={18} />
                    Join Hub
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-12">
            <section>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    Active Rooms
                  </h2>
                  <Link 
                    to={`/c/${id}/s`}
                    className="text-[10px] font-bold text-gray-400 hover:text-brand-indigo transition-colors uppercase tracking-[0.15em]"
                  >
                    View All →
                  </Link>
                </div>
                {isAdmin && (
                  <button 
                    onClick={() => navigate(`/rm/new?community_id=${id}`)}
                    className="text-[10px] font-bold text-brand-indigo hover:text-brand-indigo/80 bg-brand-indigo/5 px-4 py-2 rounded-xl transition-colors uppercase tracking-[0.15em]"
                  >
                    + Start Room
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {communityRooms.length > 0 ? (
                  communityRooms.map((room) => (
                    <RoomCard key={room.id} room={room} />
                  ))
                ) : (
                  <div className="col-span-full py-16 text-center bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-100 flex flex-col items-center">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-gray-300 mb-4 shadow-sm">
                      <Plus size={24} />
                    </div>
                    <p className="text-gray-400 font-bold text-[10px] uppercase tracking-[0.15em]">No active sessions right now</p>
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">Trending Discussions</h2>
                <Link 
                  to={`/c/${id}/d`}
                  className="text-[10px] font-bold text-gray-400 hover:text-brand-indigo transition-colors uppercase tracking-[0.15em]"
                >
                  View All →
                </Link>
              </div>
              
              <div className="space-y-4">
                {communityDiscussionsList.length > 0 ? (
                  communityDiscussionsList.map((disc) => (
                    <DiscussionCard key={disc.id} discussion={disc} compact />
                  ))
                ) : (
                  <div className="py-16 text-center bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-100">
                    <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest">No discussions started yet</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="space-y-8">
            <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">About this Hub</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-8 font-medium">
                {community.description || community.tagline || "Welcome to our community! Join us to participate in discussions, attend live sessions, and collaborate with like-minded individuals."}
              </p>
              <div className="space-y-4 pt-8 border-t border-gray-50">
                 <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Founded</span>
                    <span className="text-[10px] font-bold text-gray-900 uppercase tracking-widest">{new Date(community.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                 </div>
                 <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Privacy</span>
                    <span className={cn(
                      "text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-widest",
                      community.privacy === 'private' ? "bg-amber-50 text-amber-600 border border-amber-100" : "bg-green-50 text-green-600 border border-green-100"
                    )}>
                      {community.privacy || 'Public'}
                    </span>
                 </div>
                 <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</span>
                    <span className="text-[10px] font-bold text-brand-indigo bg-brand-indigo/5 px-2 py-0.5 rounded uppercase tracking-widest">{community.category || 'General'}</span>
                 </div>
              </div>
            </div>

            <div className="bg-brand-indigo rounded-[2.5rem] p-8 text-white shadow-2xl shadow-brand-indigo/20 relative overflow-hidden group">
              <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] group-hover:scale-110 transition-transform"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-3">Community Growth</h3>
                <p className="text-indigo-100/80 text-sm font-medium mb-6 leading-relaxed">This community has grown by 12% this week. Invite more members to keep the momentum!</p>
                <button 
                  onClick={handleInvite}
                  className="w-full py-4 bg-white text-brand-indigo font-bold rounded-2xl text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all shadow-lg"
                >
                  Invite Members
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityHub;
